/**
 * PSV-1912 — shared hover tooltip.
 *
 * One instance per scene, reused by every HUD surface: show(text, x, y)
 * paints a bordered box near the anchor, clamped into the viewport so it
 * never overflows; hide() removes it. Pure render — callers own their
 * hover detection (each panel already runs container-local pointermove).
 */

import Phaser from 'phaser';

const BG = 0x0b101c;
const BG_ALPHA = 0.96;
const BORDER = 0x35c4f0;
const TEXT_COLOR = '#dfe9f5';
const PAD_X = 8;
const PAD_Y = 6;
const DEPTH = 40; // above every panel and modal
const EDGE_MARGIN = 8;

export class Tooltip {
  private readonly scene: Phaser.Scene;
  private container: Phaser.GameObjects.Container | null = null;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /** Show at (x, y) screen coords; flips/clamps to stay inside the view. */
  show(text: string, x: number, y: number, maxWidth = 320): void {
    this.hide();
    const cam = this.scene.cameras.main;
    const label = this.scene.add.text(PAD_X, PAD_Y, text, {
      fontSize: '11px',
      color: TEXT_COLOR,
      lineSpacing: 4,
      wordWrap: { width: maxWidth },
    });
    const w = label.width + PAD_X * 2;
    const h = label.height + PAD_Y * 2;
    const bg = this.scene.add.graphics();
    bg.fillStyle(BG, BG_ALPHA);
    bg.fillRoundedRect(0, 0, w, h, 6);
    bg.lineStyle(1, BORDER, 0.75);
    bg.strokeRoundedRect(0, 0, w, h, 6);

    const px = Math.max(EDGE_MARGIN, Math.min(x, cam.width - w - EDGE_MARGIN));
    const py = Math.max(EDGE_MARGIN, Math.min(y, cam.height - h - EDGE_MARGIN));
    this.container = this.scene.add
      .container(px, py, [bg, label])
      .setScrollFactor(0)
      .setDepth(DEPTH);
  }

  hide(): void {
    this.container?.destroy(true);
    this.container = null;
  }

  destroy(): void {
    this.hide();
  }
}
